import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Progress } from '../ui/progress';
import { DollarSign, Users, TrendingUp, Target, Info, Calendar, Edit3, Lightbulb } from 'lucide-react';

const GOAL_TYPES = [
    {
        key: 'revenue',
        label: 'Revenue',
        icon: DollarSign,
        color: 'text-green-400',
        prefix: '$',
        help: 'Total revenue from all sources. Focus on building your customer base first.',
        industryAvg: 8, // 8% monthly growth for early stage
        conservativeGrowth: 5,
        aggressiveGrowth: 15
    },
    {
        key: 'users',
        label: 'New Users',
        icon: Users,
        color: 'text-blue-400',
        help: 'Number of new users acquired. Start with organic growth and word-of-mouth.',
        industryAvg: 12, // 12% monthly growth for early stage
        conservativeGrowth: 8,
        aggressiveGrowth: 25
    },
    {
        key: 'conversion',
        label: 'Conversion Rate',
        icon: TrendingUp,
        color: 'text-purple-400',
        suffix: '%',
        help: 'Overall conversion rate. Focus on improving your product-market fit.',
        industryAvg: 1.2, // 1.2% average for early stage
        conservativeGrowth: 0.2,
        aggressiveGrowth: 0.8
    },
    {
        key: 'projects',
        label: 'Active Projects',
        icon: Target,
        color: 'text-yellow-400',
        help: 'Number of active projects. Quality over quantity in the early stages.',
        industryAvg: 10, // 10% monthly growth for early stage
        conservativeGrowth: 6,
        aggressiveGrowth: 20
    }
];

const TIME_PERIODS = [
    { value: 'month', label: 'This Month', months: 1 },
    { value: 'quarter', label: 'This Quarter', months: 3 },
    { value: '6months', label: '6 Months', months: 6 },
    { value: 'year', label: 'This Year', months: 12 }
];

// Realistic current values for a 2-month-old business
const REALISTIC_CURRENT = {
    revenue: 1200, // $1,200 total revenue
    users: 45, // 45 total users
    conversion: 1.8, // 1.8% conversion rate
    projects: 12 // 12 active projects
};

// Smart target suggestions based on current performance
const generateSmartTargets = (current, goalType, period) => {
    const { industryAvg, conservativeGrowth, aggressiveGrowth } = goalType;
    const months = TIME_PERIODS.find(p => p.value === period)?.months || 1;

    // Calculate growth rates based on current performance
    const currentGrowthRate = goalType.key === 'conversion' ? 0.1 : 8; // Assume modest current growth
    const isPerformingWell = currentGrowthRate > industryAvg;

    // Adjust growth rates based on current performance
    const conservative = isPerformingWell ? conservativeGrowth * 0.8 : conservativeGrowth;
    const aggressive = isPerformingWell ? aggressiveGrowth * 1.2 : aggressiveGrowth;

    // Calculate targets
    const conservativeTarget = goalType.key === 'conversion'
        ? current + (conservative * months)
        : current * Math.pow(1 + conservative / 100, months);

    const aggressiveTarget = goalType.key === 'conversion'
        ? current + (aggressive * months)
        : current * Math.pow(1 + aggressive / 100, months);

    const industryTarget = goalType.key === 'conversion'
        ? current + (industryAvg * months)
        : current * Math.pow(1 + industryAvg / 100, months);

    return {
        conservative: Math.round(conservativeTarget * 100) / 100,
        industry: Math.round(industryTarget * 100) / 100,
        aggressive: Math.round(aggressiveTarget * 100) / 100
    };
};

const DEFAULT_GOALS = {
    revenue: {
        current: REALISTIC_CURRENT.revenue,
        targets: { month: 1800, quarter: 5000, '6months': 12000, year: 30000 },
        growth: 8
    },
    users: {
        current: REALISTIC_CURRENT.users,
        targets: { month: 60, quarter: 150, '6months': 350, year: 800 },
        growth: 12
    },
    conversion: {
        current: REALISTIC_CURRENT.conversion,
        targets: { month: 2.0, quarter: 2.5, '6months': 3.0, year: 3.5 },
        growth: 5
    },
    projects: {
        current: REALISTIC_CURRENT.projects,
        targets: { month: 15, quarter: 35, '6months': 80, year: 180 },
        growth: 10
    }
};

// Migrate old data structure to new one
const migrateGoalsData = (oldData) => {
    if (!oldData) return DEFAULT_GOALS;

    const migrated = {};

    Object.keys(DEFAULT_GOALS).forEach(key => {
        if (oldData[key]) {
            // If old data has 'target' (singular), migrate to 'targets' (plural)
            if (oldData[key].target !== undefined && !oldData[key].targets) {
                const oldTarget = oldData[key].target;
                migrated[key] = {
                    current: oldData[key].current || DEFAULT_GOALS[key].current,
                    targets: {
                        month: oldTarget,
                        quarter: oldTarget * 3,
                        '6months': oldTarget * 6,
                        year: oldTarget * 12
                    },
                    growth: oldData[key].growth || DEFAULT_GOALS[key].growth
                };
            } else {
                // Use existing data if it has the new structure
                migrated[key] = {
                    ...DEFAULT_GOALS[key],
                    ...oldData[key]
                };
            }
        } else {
            // Use default if no data exists
            migrated[key] = DEFAULT_GOALS[key];
        }
    });

    return migrated;
};

const GoalsSettings = React.forwardRef(({ onChanges }, ref) => {
    const [goals, setGoals] = useState(DEFAULT_GOALS);
    const [editing, setEditing] = useState(null);
    const [editValue, setEditValue] = useState('');
    const [selectedPeriod, setSelectedPeriod] = useState('month');
    const [showPeriodDropdown, setShowPeriodDropdown] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState({});

    // Expose functions via ref
    React.useImperativeHandle(ref, () => ({
        handleSave,
        handleReset
    }));

    useEffect(() => {
        try {
            const saved = localStorage.getItem('goalsPolished');
            if (saved) {
                const parsedData = JSON.parse(saved);
                const migratedData = migrateGoalsData(parsedData);
                setGoals(migratedData);
            }
        } catch (error) {
            console.error('Error loading goals data:', error);
            setGoals(DEFAULT_GOALS);
        }
    }, []);

    useEffect(() => {
        try {
            const saved = localStorage.getItem('goalsPolished');
            const currentData = JSON.stringify(goals);
            if (onChanges) {
                onChanges(currentData !== saved);
            }
        } catch (error) {
            console.error('Error checking for changes:', error);
            if (onChanges) {
                onChanges(false);
            }
        }
    }, [goals, onChanges]);

    const handleEdit = (key) => {
        try {
            const target = goals[key]?.targets?.[selectedPeriod];
            if (target !== undefined) {
                setEditing(key);
                setEditValue(target.toString());
            } else {
                console.error(`No target found for ${key} in period ${selectedPeriod}`);
            }
        } catch (error) {
            console.error('Error in handleEdit:', error);
        }
    };

    const handleEditChange = (e) => {
        setEditValue(e.target.value);
    };

    const handleEditSave = (key) => {
        try {
            const numValue = Number(editValue);
            if (!isNaN(numValue) && numValue >= 0) {
                setGoals(prev => ({
                    ...prev,
                    [key]: {
                        ...prev[key],
                        targets: { ...prev[key].targets, [selectedPeriod]: numValue }
                    }
                }));
            }
        } catch (error) {
            console.error('Error saving edit:', error);
        }
        setEditing(null);
    };

    const handleEditCancel = () => {
        setEditing(null);
    };

    const handleSave = () => {
        try {
            localStorage.setItem('goalsPolished', JSON.stringify(goals));
            if (onChanges) {
                onChanges(false);
            }
        } catch (error) {
            console.error('Error saving goals:', error);
        }
    };

    const handleReset = () => {
        try {
            const saved = localStorage.getItem('goalsPolished');
            if (saved) {
                const parsedData = JSON.parse(saved);
                const migratedData = migrateGoalsData(parsedData);
                setGoals(migratedData);
            } else {
                setGoals(DEFAULT_GOALS);
            }
            if (onChanges) {
                onChanges(false);
            }
        } catch (error) {
            console.error('Error resetting goals:', error);
            setGoals(DEFAULT_GOALS);
            if (onChanges) {
                onChanges(false);
            }
        }
    };

    const handleSuggestion = (key, suggestionType) => {
        const goalType = GOAL_TYPES.find(g => g.key === key);
        const current = goals[key]?.current || 0;
        const suggestions = generateSmartTargets(current, goalType, selectedPeriod);
        const suggestedValue = suggestions[suggestionType];

        setGoals(prev => ({
            ...prev,
            [key]: {
                ...prev[key],
                targets: { ...prev[key].targets, [selectedPeriod]: suggestedValue }
            }
        }));
    };

    const formatNumber = (num, prefix = '', suffix = '') => {
        if (num >= 1000000) return `${prefix}${(num / 1000000).toFixed(1)}M${suffix}`;
        if (num >= 1000) return `${prefix}${(num / 1000).toFixed(1)}K${suffix}`;
        return `${prefix}${num}${suffix}`;
    };

    const selectedPeriodLabel = TIME_PERIODS.find(p => p.value === selectedPeriod)?.label || 'This Month';

    return (
        <div className="space-y-10 max-w-4xl mx-auto">
            <div className="flex items-center justify-between mb-2">
                <div>
                    <h2 className="text-2xl font-bold text-white">Goals & Targets</h2>
                    <p className="text-zinc-400 mt-1 text-base">Set and track your business objectives. Get smart suggestions based on your performance.</p>
                </div>
            </div>

            {/* Time Period Selector */}
            <div className="flex items-center gap-4 p-4 bg-zinc-900/50 border border-zinc-800 rounded-xl">
                <Calendar className="w-5 h-5 text-zinc-400" />
                <span className="text-zinc-300 font-medium">Time Period:</span>
                <div className="relative">
                    <button
                        onClick={() => setShowPeriodDropdown(!showPeriodDropdown)}
                        className="flex items-center gap-2 px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white hover:bg-zinc-700 transition-colors min-w-[180px]"
                    >
                        <span>{selectedPeriodLabel}</span>
                        <svg className={`w-4 h-4 transition-transform ${showPeriodDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>
                    {showPeriodDropdown && (
                        <div className="absolute top-full left-0 mt-1 w-full bg-zinc-800 border border-zinc-700 rounded-lg shadow-lg z-50">
                            {TIME_PERIODS.map(period => (
                                <button
                                    key={period.value}
                                    onClick={() => {
                                        setSelectedPeriod(period.value);
                                        setShowPeriodDropdown(false);
                                    }}
                                    className={`w-full px-4 py-2 text-left text-white hover:bg-zinc-700 transition-colors first:rounded-t-lg last:rounded-b-lg ${selectedPeriod === period.value ? 'bg-blue-600/20 text-blue-400' : ''
                                        }`}
                                >
                                    {period.label}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                {GOAL_TYPES.map(({ key, label, icon: Icon, color, prefix, suffix, help }) => {
                    const { current, targets, growth } = goals[key] || {};
                    const target = targets?.[selectedPeriod] || 0;
                    const progress = target > 0 ? Math.min((current / target) * 100, 100) : 0;
                    const onTrack = current >= target;
                    const suggestions = generateSmartTargets(current, { key, ...GOAL_TYPES.find(g => g.key === key) }, selectedPeriod);

                    return (
                        <Card key={key} className="bg-zinc-900/80 border border-zinc-800 rounded-2xl p-6 flex flex-col gap-4 shadow-lg hover:shadow-xl transition-shadow">
                            <div className="flex items-center gap-3 mb-1">
                                <Icon className={`w-5 h-5 ${color}`} />
                                <h3 className="font-semibold text-white text-lg tracking-tight">{label}</h3>
                                <span className={`ml-auto px-2 py-0.5 text-xs rounded-full font-medium ${onTrack ? 'bg-green-600/20 text-green-400' : 'bg-yellow-600/20 text-yellow-400'}`}>
                                    {onTrack ? 'On Track' : 'Behind'}
                                </span>
                                <span className="ml-2 group relative">
                                    <Info className="w-4 h-4 text-zinc-400 opacity-70 hover:opacity-100 cursor-pointer" />
                                    <span className="absolute left-1/2 -translate-x-1/2 top-7 z-50 min-w-[180px] bg-zinc-800 text-zinc-200 text-xs rounded shadow-lg px-3 py-2 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-pre-line">
                                        {help}
                                    </span>
                                </span>
                            </div>

                            <div className="flex items-end justify-between">
                                <div>
                                    <div className="text-2xl font-bold text-white tracking-tight">
                                        {formatNumber(current, prefix, suffix)}
                                    </div>
                                    <div className="text-sm text-zinc-400">Current</div>
                                </div>
                                <div className="text-right">
                                    <div className="text-lg font-semibold text-zinc-300">
                                        {editing === key ? (
                                            <input
                                                type="number"
                                                value={editValue}
                                                onChange={handleEditChange}
                                                onBlur={() => handleEditSave(key)}
                                                onKeyDown={e => {
                                                    if (e.key === 'Enter') handleEditSave(key);
                                                    if (e.key === 'Escape') handleEditCancel();
                                                }}
                                                className="px-3 py-2 rounded-lg bg-zinc-800 border-2 border-blue-500 text-white w-24 text-right outline-none ring-2 ring-blue-500/20 transition-all text-lg font-semibold"
                                                autoFocus
                                                min="0"
                                                step={key === 'conversion' ? '0.1' : '1'}
                                            />
                                        ) : (
                                            <button
                                                onClick={() => handleEdit(key)}
                                                className="group flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-zinc-800/80 transition-all duration-200 border border-transparent hover:border-blue-500/30"
                                            >
                                                <span className="text-lg font-semibold text-zinc-300 group-hover:text-blue-400 transition-colors">
                                                    {formatNumber(target, prefix, suffix)}
                                                </span>
                                                <Edit3 className="w-4 h-4 text-zinc-500 group-hover:text-blue-400 transition-colors opacity-0 group-hover:opacity-100" />
                                            </button>
                                        )}
                                    </div>
                                    <div className="text-sm text-zinc-400">Target</div>
                                </div>
                            </div>

                            {/* Smart Suggestions */}
                            <div className="mt-2">
                                <button
                                    onClick={() => setShowSuggestions(prev => ({ ...prev, [key]: !prev[key] }))}
                                    className="flex items-center gap-2 text-xs text-zinc-400 hover:text-blue-400 transition-colors"
                                >
                                    <Lightbulb className="w-3 h-3" />
                                    Smart suggestions
                                    <svg className={`w-3 h-3 transition-transform ${showSuggestions[key] ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>

                                {showSuggestions[key] && (
                                    <div className="mt-3 p-3 bg-zinc-800/50 rounded-lg space-y-2">
                                        <div className="text-xs text-zinc-400 mb-2">Based on your current performance:</div>
                                        <div className="flex flex-wrap gap-2">
                                            <button
                                                onClick={() => handleSuggestion(key, 'conservative')}
                                                className="px-2 py-1 text-xs bg-yellow-600/20 text-yellow-400 rounded hover:bg-yellow-600/30 transition-colors"
                                            >
                                                Conservative: {formatNumber(suggestions.conservative, prefix, suffix)}
                                            </button>
                                            <button
                                                onClick={() => handleSuggestion(key, 'industry')}
                                                className="px-2 py-1 text-xs bg-blue-600/20 text-blue-400 rounded hover:bg-blue-600/30 transition-colors"
                                            >
                                                Industry: {formatNumber(suggestions.industry, prefix, suffix)}
                                            </button>
                                            <button
                                                onClick={() => handleSuggestion(key, 'aggressive')}
                                                className="px-2 py-1 text-xs bg-green-600/20 text-green-400 rounded hover:bg-green-600/30 transition-colors"
                                            >
                                                Aggressive: {formatNumber(suggestions.aggressive, prefix, suffix)}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="flex items-center gap-2 mt-1">
                                <span className="text-xs text-zinc-400">Growth:</span>
                                <span className={`text-xs font-medium ${growth >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                    {growth >= 0 ? '+' : ''}{growth}%
                                </span>
                            </div>

                            <div className="w-full bg-zinc-800 rounded-full h-2 mt-2">
                                <div
                                    className={`h-2 rounded-full transition-all duration-500 ${onTrack ? 'bg-green-500' : 'bg-yellow-500'}`}
                                    style={{ width: `${progress}%` }}
                                />
                            </div>
                        </Card>
                    );
                })}
            </div>


        </div>
    );
});

export default GoalsSettings;